import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { login, resetAuthError } from '../store/slices/authSlice';
import { RootState } from '../store';
import Layout from '../components/layout/Layout';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { refreshUserInfo } from '../hooks/useUserInfo';
import { redirectAfterLogin } from '../utils/redirectUtils';
import { ReactComponent as PasswordEyeIcon } from '../assets/password_eye.svg';
import { ReactComponent as LoginCheckIcon } from '../assets/login-check.svg';

interface LoginFormValues {
  email: string;
  password: string;
}

const Login: React.FC = () => {
  const [formValues, setFormValues] = useState<LoginFormValues>({
    email: '',
    password: '',
  });
  const [formErrors, setFormErrors] = useState<Partial<LoginFormValues>>({});
  const [showPassword, setShowPassword] = useState(false); // 기본값: 비밀번호 가림
  const [keepLoggedIn, setKeepLoggedIn] = useState(false); // 로그인 유지 상태
  const [loginError, setLoginError] = useState(''); // 로그인 에러 메시지

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loading, error, isAuthenticated } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    // 이미 로그인된 경우 리다이렉트 처리
    if (isAuthenticated) {
      // 로그인 성공 시 사용자 정보 새로고침
      refreshUserInfo();
      
      // URL 쿼리 파라미터 또는 저장된 리다이렉트 경로로 이동
      const redirectPath = searchParams.get('redirect');
      if (redirectPath) {
        navigate(decodeURIComponent(redirectPath), { replace: true });
      } else {
        redirectAfterLogin(navigate);
      }
      return;
    }
    
    // Redux 에러가 있으면 로그인 에러로 설정
    if (error) {
      setLoginError(error);
    }
    
    // 컴포넌트 마운트 시 에러 초기화
    dispatch(resetAuthError());
    
    // 토큰이 있으면 사용자 프로필 가져오기
    // if (localStorage.getItem('access_token') && !isAuthenticated) {
    //   dispatch(fetchUserProfile());
    // }
  }, [isAuthenticated, navigate, dispatch, error, searchParams]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // 이메일 필드에서 한글 입력 제한
    if (name === 'email') {
      // 한글 제거 및 영어/숫자/기호만 허용
      const filteredValue = value.replace(/[ㄱ-ㅎ가-힣]/g, '');
      setFormValues({
        ...formValues,
        [name]: filteredValue,
      });
    } else {
      setFormValues({
        ...formValues,
        [name]: value,
      });
    }
    
    // 입력 시 해당 필드 에러 제거
    if (formErrors[name as keyof LoginFormValues]) {
      setFormErrors({
        ...formErrors,
        [name]: '',
      });
    }
    
    // 입력 시 로그인 에러 제거
    if (loginError) {
      setLoginError('');
    }
  };

  // 이메일 입력 시 한글 키보드 입력 차단
  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // IME 입력 차단 (한글 조합 중 차단)
    if (e.nativeEvent.isComposing) {
      e.preventDefault();
      return;
    }
  };

  // 이메일 입력 시 IME 조합 입력 차단
  const handleEmailComposition = (e: React.CompositionEvent<HTMLInputElement>) => {
    // 조합 입력(한글) 차단
    e.preventDefault();
  };

  const validateForm = (): boolean => {
    const errors: Partial<LoginFormValues> = {};
    let isValid = true;

    if (!formValues.email) {
      errors.email = '이메일을 입력해주세요';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formValues.email)) {
      errors.email = '유효한 이메일 주소를 입력해주세요';
      isValid = false;
    }

    if (!formValues.password) {
      errors.password = '비밀번호를 입력해주세요';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      // 로그인 에러 초기화
      setLoginError('');
      
      // 실제 API 호출
      dispatch(login({ 
        email: formValues.email, 
        password: formValues.password,
        rememberMe: keepLoggedIn
      }));
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleKeepLoggedIn = () => {
    setKeepLoggedIn(!keepLoggedIn);
  };

  return (
    <Layout>
      <MainContainer>
        <SloganSection>
          <SloganText>대한민국의학한림원 × 투비닥터</SloganText>
          <SloganTitle>MEDICAL AI CAMPUS.</SloganTitle>
        </SloganSection>
        
        <FormContainer>
          <form onSubmit={handleSubmit}>
            {/* 이메일 입력 */}
            <FormGroup hasBottomMargin>
              <FormLabel>이메일</FormLabel>
              <EmailInput
                type="email"
                name="email"
                placeholder="이메일을 입력해주세요."
                value={formValues.email}
                onChange={handleChange}
                onKeyDown={handleEmailKeyDown}
                onCompositionStart={handleEmailComposition}
                onCompositionUpdate={handleEmailComposition}
                onCompositionEnd={handleEmailComposition}
                className={formErrors.email ? 'error' : ''}
                autoComplete="email"
                inputMode="email"
                lang="en"
              />
              {formErrors.email && <InputHelp error>{formErrors.email}</InputHelp>}
            </FormGroup>
            
            {/* 비밀번호 입력 */}
            <FormGroup>
              <FormLabel>비밀번호</FormLabel>
              <PasswordInputWrapper>
                <PasswordInput
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="비밀번호를 입력해주세요."
                  value={formValues.password}
                  onChange={handleChange}
                  className={formErrors.password ? 'error' : ''}
                />
                <EyeIconWrapper onClick={togglePasswordVisibility}>
                  <PasswordEyeIcon width={22} height={22} />
                </EyeIconWrapper>
              </PasswordInputWrapper>
              {formErrors.password && <InputHelp error>{formErrors.password}</InputHelp>}
            </FormGroup>
            
            {/* 로그인 유지 체크박스 */}
            <KeepLoggedInWrapper>
              <CheckboxWrapper onClick={toggleKeepLoggedIn}>
                {/* 실제 input 제거 - DOM 변화 방지 */}
                <CheckboxCustom checked={keepLoggedIn}>
                  <LoginCheckIcon style={{opacity: keepLoggedIn ? 1 : 0}} />
                </CheckboxCustom>
              </CheckboxWrapper>
              <CheckboxLabel onClick={toggleKeepLoggedIn}>
                로그인 유지
              </CheckboxLabel>
            </KeepLoggedInWrapper>
            
            {/* 로그인 에러 메시지 */}
            {loginError && (
              <LoginErrorMessage>
                {loginError}
              </LoginErrorMessage>
            )}
            
            {/* 로그인 버튼 */}
            <LoginButton 
              type="submit" 
              disabled={loading}
            >
              {loading ? '처리 중...' : '로그인'}
            </LoginButton>
            
            {/* 링크 버튼들 */}
            <LinkButtonsRow>
              <LinkButton to="/signup">회원가입</LinkButton>
              <LinkButton to="/forgot-password">비밀번호 찾기</LinkButton>
            </LinkButtonsRow>
          </form>
        </FormContainer>
      </MainContainer>
    </Layout>
  );
};

// 스타일 컴포넌트
const MainContainer = styled.main`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 64px;
  max-width: ${({ theme }) => theme.layout.containerWidth};
  margin: 0 auto;
  padding: 128px ${({ theme }) => theme.layout.containerPaddingMobile} 160px;
`;

const SloganSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SloganText = styled.p`
  font-size: 28px;
  font-weight: 400;
  line-height: 1.36;
  letter-spacing: -2.36%;
  color: #171719;
`;

const SloganTitle = styled.h1`
  font-size: 40px;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: -2.82%;
  color: #171719;
  margin-top: 0;
`;

const FormContainer = styled.div`
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div<{ hasBottomMargin?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  margin-bottom: ${props => props.hasBottomMargin ? '36px' : '0'};
`;

const FormLabel = styled.label`
  font-weight: 600;
  font-size: 14px;
  line-height: 1.429em;
  color: rgba(46, 47, 51, 0.88);
`;

// 이메일 입력 컴포넌트
const EmailInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  border-radius: 12px;
  font-size: 16px;
  line-height: 1.5em;
  outline: none;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.03);
  transition: border-color 0.2s;
  box-sizing: border-box;
  
  /* IME 비활성화 (한글 입력기 차단) */
  ime-mode: disabled;
  -webkit-ime-mode: disabled;
  -moz-ime-mode: disabled;
  -ms-ime-mode: disabled;
  
  /* 영어 입력만 허용하는 스타일 */
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  
  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }
  
  &.error {
    border-color: #ff5252;
  }
  
  &:focus {
    border-color: #448181;
    ime-mode: disabled;
  }
`;

// 비밀번호 입력 관련 컴포넌트들
const PasswordInputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const PasswordInput = styled.input`
  width: 100%;
  padding: 12px;
  padding-right: 44px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  border-radius: 12px;
  font-size: 16px;
  line-height: 1.5em;
  outline: none;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.03);
  box-sizing: border-box;
  transition: border-color 0.2s;
  
  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }
  
  &.error {
    border-color: #ff5252;
  }
  
  &:focus {
    border-color: #448181;
  }
`;

const EyeIconWrapper = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 0.8;
  }
  
  &:active {
    opacity: 0.6;
  }
  
  svg {
    pointer-events: none;
  }
`;

// 로그인 유지 체크박스 스타일
const KeepLoggedInWrapper = styled.div`
  width: 335px;
  height: 20px;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 10px;
  display: inline-flex;
  margin-top: 8px;
  margin-bottom: 0;
  flex-shrink: 0;
  position: relative;
`;

const CheckboxWrapper = styled.div`
  width: 20px;
  height: 20px;
  padding: 2px;
  position: relative;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  display: inline-flex;
  cursor: pointer;
  flex-shrink: 0;
  box-sizing: border-box;
`;

const CheckboxCustom = styled.div<{ checked: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 5px;
  border: 1.5px solid rgba(112, 115, 124, 0.22);
  background-color: ${props => props.checked ? '#448181' : 'transparent'};
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  box-sizing: border-box;
  contain: layout style paint;
  
  ${props => props.checked && `
    border-color: #448181;
  `}
  
  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    transition: none;
  }
`;

const CheckboxLabel = styled.div`
  flex: 1 1 0;
  color: #171719;
  font-size: 14px;
  font-family: 'Pretendard JP', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 400;
  line-height: 20.01px;
  letter-spacing: 0.20px;
  word-wrap: break-word;
  cursor: pointer;
  user-select: none;
  align-self: flex-start;
`;

// 로그인 에러 메시지
const LoginErrorMessage = styled.div`
  align-self: stretch;
  color: #FF4242;
  font-size: 14px;
  font-family: 'Pretendard JP', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 400;
  line-height: 20.01px;
  letter-spacing: 0.20px;
  word-wrap: break-word;
  margin-top: 36px;
  margin-bottom: 0;
`;

const InputHelp = styled.p<{ error?: boolean }>`
  font-size: 12px;
  line-height: 1.334em;
  color: ${props => props.error ? '#ff5252' : 'rgba(55, 56, 60, 0.61)'};
  margin-top: 4px;
`;

const LoginButton = styled.button`
  width: 100%;
  background-color: #448181;
  color: #FFFFFF;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 13px;
  line-height: 138.5%;
  letter-spacing: 0.02em;
  font-family: var(--font-family-base, 'Pretendard JP', -apple-system, BlinkMacSystemFont, sans-serif);
  cursor: pointer;
  padding: 12px 28px;
  margin-top: 44px;
  margin-bottom: 36px;
  min-height: 48px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex-shrink: 0;
  text-align: left;
  
  &:disabled {
    background-color: #F4F4F5;
    color: rgba(55, 56, 60, 0.28);
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    background-color: #3a6f6f;
  }
  
  &:active:not(:disabled) {
    background-color: #2d5a5a;
  }
`;

const LinkButtonsRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 96px;
`;

const LinkButton = styled(Link)`
  color: #448181;
  text-decoration: none;
  font-size: 13px;
  font-weight: 600;
  line-height: 138.5%;
  letter-spacing: 0.02em;
  font-family: var(--font-family-base, 'Pretendard JP', -apple-system, BlinkMacSystemFont, sans-serif);
  text-align: left;
`;

export default Login;
